import {nextTick} from "vue";

// Every input control marks its own field with this class while it carries a validation error.
const INVALID_FIELD_SELECTOR = '.is-invalid';

// A folded accordion never reports its unfolding if the transition is cancelled or was never
// started, so waiting for it is given an upper bound. Bootstrap's own transition takes 350ms.
const COLLAPSE_TIMEOUT_IN_MS = 600;

/**
 * Brings the first field that failed validation into view and puts the cursor in it.
 *
 * A form that refuses to submit is otherwise silent: the field says what is wrong, but on a form
 * as long as a content page column that field is rarely on screen, and all the user sees is a save
 * button that does nothing.
 *
 * The first field in document order is the one taken, which is the first one the user would reach
 * reading downwards. Fields folded away inside a collapsed accordion count as well: their section
 * is unfolded first, since scrolling to something that is not displayed moves nothing.
 *
 * @param root Limits the search, so a page holding more than one form scrolls to a field of the
 *             form that was actually submitted. Defaults to the whole document.
 */
async function scrollToFirstInvalidField(root?: HTMLElement | null): Promise<void> {
    // The class is applied by the re-render that follows the failed validation, so the DOM only
    // carries it once Vue has flushed.
    await nextTick();

    const field = (root ?? document).querySelector<HTMLElement>(INVALID_FIELD_SELECTOR);

    if (!field) {
        return;
    }

    await expandCollapsedAncestors(field);

    // Instant, not smooth. A smooth scrollIntoView is silently dropped by Chrome when the scroll
    // container sits inside an offcanvas, which is exactly where the longest forms live: the call
    // returns without error and nothing moves. Verified against the column offcanvas, where the
    // same call with 'auto' scrolls and with 'smooth' does nothing at all.
    field.scrollIntoView({behavior: 'auto', block: 'center'});
    // Focus would scroll the field into view again on its own terms, undoing the centering above.
    field.focus({preventScroll: true});
}

/**
 * Unfolds every collapsed accordion the element is folded away in, and waits until they are open.
 *
 * Waiting matters: the element has no position of its own while its section is still unfolding,
 * and scrolling to it during the transition lands somewhere above where it ends up.
 *
 * The section is opened by clicking its own toggle rather than through Bootstrap's Collapse API on
 * purpose. main.ts imports the whole of Bootstrap; importing `bootstrap/js/dist/collapse` here
 * would load a *second* copy of the module, and each copy registers its own document-wide handler
 * for [data-bs-toggle="collapse"]. Every header click would then be handled twice, and since
 * toggling is not idempotent the two cancel out — the accordions in this app could be opened but
 * never closed again. Clicking the toggle uses whichever instance the app already has.
 */
async function expandCollapsedAncestors(element: HTMLElement): Promise<void> {
    const collapsed = [] as HTMLElement[];

    let ancestor = element.parentElement?.closest<HTMLElement>('.collapse:not(.show)') ?? null;
    while (ancestor) {
        collapsed.push(ancestor);
        ancestor = ancestor.parentElement?.closest<HTMLElement>('.collapse:not(.show)') ?? null;
    }

    await Promise.all(collapsed.map((collapseElement: HTMLElement) => new Promise<void>(resolve => {
        const toggle = collapseElement.id
            ? document.querySelector<HTMLElement>(
                `[data-bs-toggle="collapse"][data-bs-target="#${CSS.escape(collapseElement.id)}"]`)
            : null;

        // A collapse nobody can open from the page is left as it is; scrolling still happens, it
        // just cannot bring this particular field into view.
        if (!toggle) {
            resolve();
            return;
        }

        const timeout = window.setTimeout(resolve, COLLAPSE_TIMEOUT_IN_MS);

        collapseElement.addEventListener('shown.bs.collapse', () => {
            window.clearTimeout(timeout);
            resolve();
        }, {once: true});

        toggle.click();
    })));
}

export {
    scrollToFirstInvalidField,
};
